import { useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import AdminLayout from '../../components/layout/AdminLayout'

// Helper function to change lead status
const changeLeadStatus = (leadId, newStatus) => {
  router.patch(`/leads/${leadId}/change_status`, { status: newStatus }, {
    preserveState: true,
    preserveScroll: true
  })
}

// Helper function to translate project type
const translateProjectType = (type) => {
  const translations = {
    'buyer': 'Acheteur',
    'seller': 'Vendeur',
    'tenant': 'Locataire',
    'landlord': 'Propriétaire',
    'achat': 'Achat',
    'vente': 'Vente',
    'location': 'Location'
  }
  return translations[type] || type
}

export default function LeadsIndex({ leads, pagination, filters, stats }) {
  const [searchQuery, setSearchQuery] = useState(filters.q || '')
  const [selectedStatus, setSelectedStatus] = useState(filters.status || '')
  const [selectedType, setSelectedType] = useState(filters.project_type || '')

  const handleSearch = (e) => {
    e.preventDefault()
    applyFilters({ q: searchQuery })
  }

  const applyFilters = (newFilters) => {
    router.get('/leads', {
      ...filters,
      ...newFilters,
      page: 1 // Reset to first page when filtering
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const clearFilters = () => {
    setSearchQuery('')
    setSelectedStatus('')
    setSelectedType('')
    router.get('/leads', {}, { preserveState: true })
  }

  const hasActiveFilters = filters.q || filters.status || filters.project_type

  return (
    <AdminLayout>
      <Head title="Leads" />

      <div>
        <div className="mx-auto max-w-7xl">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Leads</h1>
            <p className="mt-2 text-sm text-gray-600">
              Gérez et suivez vos leads
            </p>
          </div>

          {/* Stats Cards */}
          <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
            <StatsCard title="Total" value={stats.total} color="blue" />
            <StatsCard title="Nouveau" value={stats.new} color="gray" />
            <StatsCard title="Contacté" value={stats.contacted} color="yellow" />
            <StatsCard title="Qualifié" value={stats.qualified} color="indigo" />
            <StatsCard title="Converti" value={stats.converted} color="green" />
          </div>

          {/* Filters */}
          <div className="mb-6 bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Filtres et recherche</h2>
            <form onSubmit={handleSearch} className="space-y-6">
              {/* Search */}
              <div>
                <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
                  Rechercher un lead
                </label>
                <div className="flex gap-3">
                  <input
                    type="text"
                    id="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Nom, email ou téléphone..."
                    className="flex-1 rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-primary-500 focus:ring-2 focus:ring-offset-1 focus:outline-none"
                  />
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                  >
                    Rechercher
                  </button>
                </div>
              </div>

              {/* Filters Row */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {/* Status Filter */}
                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                    Statut
                  </label>
                  <select
                    id="status"
                    value={selectedStatus}
                    onChange={(e) => {
                      setSelectedStatus(e.target.value)
                      applyFilters({ status: e.target.value })
                    }}
                    className="w-full rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-primary-500 focus:ring-2 focus:ring-offset-1 focus:outline-none"
                  >
                    <option value="">Tous les statuts</option>
                    <option value="new">Nouveau</option>
                    <option value="contacted">Contacté</option>
                    <option value="qualified">Qualifié</option>
                    <option value="converted">Converti</option>
                    <option value="lost">Perdu</option>
                  </select>
                </div>

                {/* Project Type Filter */}
                <div>
                  <label htmlFor="project_type" className="block text-sm font-medium text-gray-700 mb-2">
                    Type de projet
                  </label>
                  <select
                    id="project_type"
                    value={selectedType}
                    onChange={(e) => {
                      setSelectedType(e.target.value)
                      applyFilters({ project_type: e.target.value })
                    }}
                    className="w-full rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-primary-500 focus:ring-2 focus:ring-offset-1 focus:outline-none"
                  >
                    <option value="">Tous les types</option>
                    <option value="buyer">Acheteur</option>
                    <option value="seller">Vendeur</option>
                    <option value="tenant">Locataire</option>
                    <option value="landlord">Propriétaire</option>
                  </select>
                </div>

                {/* Clear Filters Button */}
                {hasActiveFilters && (
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                    >
                      Réinitialiser
                    </button>
                  </div>
                )}
              </div>
            </form>
          </div>

          {/* Leads Table */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nom
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Budget
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {leads.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                      {hasActiveFilters ? 'Aucun lead trouvé avec ces filtres' : 'Aucun lead pour le moment'}
                    </td>
                  </tr>
                ) : (
                  leads.map((lead) => (
                    <tr
                      key={lead.id}
                      className="hover:bg-blue-50 transition-colors"
                    >
                      <td
                        className="px-6 py-4 whitespace-nowrap cursor-pointer"
                        onClick={() => router.visit(`/leads/${lead.id}`)}
                      >
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {lead.full_name}
                            </div>
                            {lead.qr_code_name && (
                              <div className="text-xs text-gray-500">
                                via {lead.qr_code_name}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td
                        className="px-6 py-4 whitespace-nowrap cursor-pointer"
                        onClick={() => router.visit(`/leads/${lead.id}`)}
                      >
                        <div className="text-sm text-gray-900">{lead.email}</div>
                        {lead.phone && (
                          <div className="text-sm text-gray-500">{lead.phone}</div>
                        )}
                      </td>
                      <td
                        className="px-6 py-4 whitespace-nowrap cursor-pointer"
                        onClick={() => router.visit(`/leads/${lead.id}`)}
                      >
                        <span className="inline-flex px-2 text-xs font-semibold leading-5 text-gray-800 bg-gray-100 rounded-full">
                          {translateProjectType(lead.project_type)}
                        </span>
                      </td>
                      <td
                        className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 cursor-pointer"
                        onClick={() => router.visit(`/leads/${lead.id}`)}
                      >
                        {lead.budget_formatted}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={lead.status}
                          onChange={(e) => changeLeadStatus(lead.id, e.target.value)}
                          className="text-sm rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-primary-500 focus:ring-2 focus:ring-offset-1 focus:outline-none transition-colors cursor-pointer w-full max-w-[140px]"
                        >
                          <option value="new">Nouveau</option>
                          <option value="contacted">Contacté</option>
                          <option value="qualified">Qualifié</option>
                          <option value="converted">Converti</option>
                          <option value="lost">Perdu</option>
                        </select>
                      </td>
                      <td
                        className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 cursor-pointer"
                        onClick={() => router.visit(`/leads/${lead.id}`)}
                      >
                        {lead.source || 'qr_code'}
                      </td>
                      <td
                        className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 cursor-pointer"
                        onClick={() => router.visit(`/leads/${lead.id}`)}
                      >
                        {new Date(lead.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Link
                          href={`/leads/${lead.id}`}
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium transition-colors"
                        >
                          <span>Voir</span>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="bg-white px-4 py-4 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => router.get(`/leads?page=${pagination.current_page - 1}`)}
                    disabled={pagination.current_page === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    ← Précédent
                  </button>
                  <button
                    onClick={() => router.get(`/leads?page=${pagination.current_page + 1}`)}
                    disabled={pagination.current_page === pagination.total_pages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Suivant →
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Page <span className="font-semibold text-gray-900">{pagination.current_page}</span> sur{' '}
                      <span className="font-semibold text-gray-900">{pagination.total_pages}</span>
                      <span className="mx-2">•</span>
                      <span className="font-semibold text-gray-900">{pagination.total_count}</span> lead{pagination.total_count > 1 ? 's' : ''} au total
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => router.get(`/leads?page=${pagination.current_page - 1}`)}
                        disabled={pagination.current_page === 1}
                        className="relative inline-flex items-center px-4 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        ← Précédent
                      </button>
                      <button
                        onClick={() => router.get(`/leads?page=${pagination.current_page + 1}`)}
                        disabled={pagination.current_page === pagination.total_pages}
                        className="relative inline-flex items-center px-4 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Suivant →
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}

// StatsCard Component
function StatsCard({ title, value, color = 'blue' }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-green-50 text-green-700',
    yellow: 'bg-yellow-50 text-yellow-700',
    indigo: 'bg-indigo-50 text-indigo-700',
    gray: 'bg-gray-50 text-gray-700',
  }

  return (
    <div className="overflow-hidden bg-white rounded-lg shadow">
      <div className="p-6">
        <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
        <dd className={`mt-2 text-3xl font-semibold ${colorClasses[color]}`}>{value}</dd>
      </div>
    </div>
  )
}

// StatusBadge Component
function StatusBadge({ status }) {
  const statusConfig = {
    new: { label: 'Nouveau', color: 'bg-gray-100 text-gray-800' },
    contacted: { label: 'Contacté', color: 'bg-yellow-100 text-yellow-800' },
    qualified: { label: 'Qualifié', color: 'bg-blue-100 text-blue-800' },
    converted: { label: 'Converti', color: 'bg-green-100 text-green-800' },
    lost: { label: 'Perdu', color: 'bg-red-100 text-red-800' },
  }

  const config = statusConfig[status] || statusConfig.new

  return (
    <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${config.color}`}>
      {config.label}
    </span>
  )
}
